import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';

import userService from '../../../services/userService';
import authService from '../../../services/authService';
import securityUtils from '../../../services/securityUtils';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { usePortalName } from '../../../hooks/usePortalName';

import {
  fetchLoggedInUserInfoAsync,
  resetUserState,
  selectLoggedInUserInfo,
  setLoggedInUserInitialized,
} from '../../../data/user.slice';

import TextInput from '../../forms/TextInput';
import DateInput from '../../forms/DateInput';

const PREFIX = 'VerifyAccountPageForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  formBody: `${PREFIX}-formBody`,
  title: `${PREFIX}-title`,
  helpText: `${PREFIX}-helpText`,
  termsConditions: `${PREFIX}-termsConditions`,
  submit: `${PREFIX}-submit`,
  backButton: `${PREFIX}-backButton`,
  row: `${PREFIX}-row`,
  dateInputWrapper: `${PREFIX}-dateInputWrapper`,
  link: `${PREFIX}-link`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    backgroundColor: 'white',
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  [`& .${classes.form}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  [`& .${classes.formBody}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(2),
  },
  [`& .${classes.row}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    width: '100%',
  },
  [`& .${classes.title}`]: {
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2.5),
    width: '100%',
  },
  [`& .${classes.helpText}`]: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
    textAlign: 'left',
  },
  [`& .${classes.termsConditions}`]: {
    textAlign: 'left',
  },
  [`& .${classes.submit}`]: {
    width: '100%',
    marginTop: theme.spacing(3),
  },
  [`& .${classes.backButton}`]: {
    position: 'absolute',
    top: theme.spacing(1),
    left: theme.spacing(1),
    width: 40,
    height: 40,
    color: theme.palette.primary.main,
  },
  [`& .${classes.dateInputWrapper}`]: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
  },
  [`& .${classes.link}`]: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    fontSize: 16,
    fontWeight: 400,
  },
}));

export default function VerifyAccountPageForm() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const requiredFieldMessage = t('common.requiredField');
  const portalName = usePortalName();
  const loggedInUser = useAppSelector(selectLoggedInUserInfo);
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      userFirstNamex: '',
      userLastName: '',
      dateOfBirth: null,
      memberId: '',
    } as any,
  });
  const [userAccountID, setUserAccountID] = useState<number>();
  const [errorMessage, setErrorMessage] = useState<string>('');

  const associateMemberWithPassenger = (userId: number, data: any) => {
    const clientBC = securityUtils.getPortalClientBC(portalName || '');
    userService
      .associateMemberUserWithPassenger(
        userId,
        data.userFirstName,
        data.userLastName,
        data.memberID,
        data.dateOfBirth,
        data.phoneNumber,
        data.address,
        data.zipCode,
        clientBC,
      )
      .subscribe({
        next: (response: any) => {
          if (response) {
            dispatch(setLoggedInUserInitialized(false));
            dispatch(fetchLoggedInUserInfoAsync());
            navigate('/home');
          }
        },
        error: (err) => {
          if (err.validationErrors) {
            let issues = '';
            if (err.validationErrors.issues) {
              err.validationErrors.issues.forEach((issue: any) => {
                issues += `${issue.code}: ${issue.message}\n`;
              });
            }
            setErrorMessage(issues || '');
          } else if (err.message) {
            setErrorMessage(String(err.message) || '');
          } else {
            setErrorMessage(String(err) || '');
          }
        },
      });
  };

  const associateFederatedMemberWithPassenger = (data: any) => {
    authService.getTokens().subscribe({
      next: (tokens) => {
        const cbUrl = securityUtils.getPortalCallbackURL(portalName || '');
        const logoUrl = securityUtils.getPortalLogoURL(portalName || '');
        const clientBC = securityUtils.getPortalClientBC(portalName || '');
        userService
          .onboardFederatedMemberUser(tokens.idToken!, cbUrl, logoUrl, clientBC)
          .subscribe({
            next: (onboardResponse: any) => {
              setUserAccountID(onboardResponse.userAccountID);
              if (onboardResponse) {
                associateMemberWithPassenger(
                  onboardResponse.userAccountID,
                  data,
                );
              } else {
                setErrorMessage(t('member.failedToCreateAccountError'));
              }
            },
            error: (err) => {
              if (err.message) {
                setErrorMessage(String(err.message) || '');
              } else {
                setErrorMessage(String(err) || '');
              }
            },
          });
      },
    });
  };

  const onSubmit = (data: any) => {
    if (isEmpty(loggedInUser) && !userAccountID) {
      associateFederatedMemberWithPassenger(data);
    } else {
      associateMemberWithPassenger(
        loggedInUser.userAccountID || userAccountID,
        data,
      );
    }
  };

  const logout = () => {
    authService.logout().then(() => {
      // This set timeout is required to give the logout time to work fully in AWS
      setTimeout(() => {
        dispatch(resetUserState());
        window.location.href = '/login';
      });
    });
  };

  return (
    <Root className={classes.container}>
      <Box className={classes.form}>
        <Typography variant="h3" className={classes.title}>
          {t('client.profile')}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)} className={classes.formBody}>
          <Box className={classes.row}>
            <TextInput
              label={t('users.firstName')}
              fullWidth
              required
              control={control}
              name="userFirstName"
              rules={{
                required: requiredFieldMessage,
              }}
            />
            <TextInput
              label={t('users.lastName')}
              fullWidth
              required
              control={control}
              name="userLastName"
              rules={{
                required: requiredFieldMessage,
              }}
            />
          </Box>

          <Box className={classes.dateInputWrapper}>
            <DateInput
              label={t('passenger.dob')}
              required
              control={control}
              name="dateOfBirth"
            />
          </Box>

          <TextInput
            label={t('member.memberId')}
            fullWidth
            required
            control={control}
            name="memberID"
            rules={{
              required: requiredFieldMessage,
            }}
          />

          {errorMessage && (
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          )}

          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.submit}
            disabled={!isValid}
            type="submit">
            {t('member.continue')}
          </Button>

          <Typography variant="body2" className={classes.termsConditions}>
            {t('member.termsAndPrivacy')}
          </Typography>
        </form>

        <Link onClick={logout} className={classes.link}>
          {t('member.backToLogin')}
        </Link>
      </Box>
    </Root>
  );
}
